import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import CustomerAssistedRegistrationApi from "../../../../infrastructure/api/customerAssistedRegistrationApi";

const FIELD_LABELS = {
  firstName: "نام",
  lastName: "نام خانوادگی",
  nationalCode: "کد ملی",
  email: "ایمیل",
  address: "آدرس",
  postalCode: "کد پستی",
  telephon: "تلفن",
  locationInput: "ورودی موقعیت",
  electricBoillNumber: "شماره قبض برق",
  electricBoillUrl: "لینک قبض برق",
  homeOwnerShipDocUrl: "لینک سند مالکیت",
  note: "توضیحات",
  plantCapacity: "ظرفیت",
  installationType: "نوع نصب",
};

const FORM_WIDE_MAX = 880;

const CustomerAssistedRegistrationCorrectionPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [profile, setProfile] = useState({});
  const [request, setRequest] = useState({});
  const [locationPreview, setLocationPreview] = useState(null);
  const [locationError, setLocationError] = useState("");

  const loadLead = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CustomerAssistedRegistrationApi.getActive();
      if (!res.isSuccess) {
        toast.error(res.message);
        setDetail(null);
        return;
      }
      if (!res.data) {
        setDetail(null);
        return;
      }
      setDetail(res.data);
      setProfile(res.data.profile || {});
      setRequest(res.data.request || {});
    } catch {
      toast.error("بارگذاری پرونده ناموفق بود.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  const correctableFields = useMemo(
    () => (detail?.correctableFields || []).map((f) => String(f).toLowerCase()),
    [detail?.correctableFields]
  );

  const isFieldEditable = useCallback(
    (field) => detail?.canEdit && correctableFields.includes(field.toLowerCase()),
    [detail?.canEdit, correctableFields]
  );

  useEffect(() => {
    const input = profile.locationInput?.trim();
    if (!input || !isFieldEditable("locationInput")) {
      setLocationPreview(null);
      setLocationError("");
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await CustomerAssistedRegistrationApi.parseLocation(input);
        if (!res.isSuccess) {
          setLocationPreview(null);
          setLocationError(res.message || "موقعیت نامعتبر است.");
          return;
        }
        setLocationError("");
        setLocationPreview({
          latitude: res.data.latitude,
          longitude: res.data.longitude,
        });
      } catch {
        setLocationPreview(null);
        setLocationError("پردازش موقعیت ناموفق بود.");
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [profile.locationInput, isFieldEditable]);

  const handleSave = async () => {
    if (!detail?.leadId) return;
    if (isFieldEditable("locationInput") && profile.locationInput?.trim() && locationError) {
      toast.error(locationError);
      return;
    }
    setSaving(true);
    try {
      const res = await CustomerAssistedRegistrationApi.saveCorrection(detail.leadId, {
        profile,
        request,
      });
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      setDetail(res.data);
      toast.success("اصلاحات ذخیره شد.");
    } catch {
      toast.error("ذخیره اصلاحات ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const handleResubmit = async () => {
    if (!detail?.leadId) return;
    setSaving(true);
    try {
      const res = await CustomerAssistedRegistrationApi.resubmit(detail.leadId);
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      setDetail(res.data);
      toast.success("اصلاحات برای بررسی مجدد ارسال شد.");
    } catch {
      toast.error("ارسال مجدد ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography sx={{ p: 3 }}>در حال بارگذاری...</Typography>;
  }

  if (!detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">پرونده‌ای برای اصلاح یا مشاهده یافت نشد.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: FORM_WIDE_MAX, mx: "auto", width: "100%" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        پرونده ثبت نماینده — {detail.leadCode}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        وضعیت: {detail.statusTitle}
      </Typography>

      {detail.correctionNote ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            دستورالعمل اصلاح کارشناس
          </Typography>
          <Typography variant="body2">{detail.correctionNote}</Typography>
          {correctableFields.length ? (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              فیلدهای قابل اصلاح:{" "}
              {correctableFields.map((f) => FIELD_LABELS[f] || f).join("، ")}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {detail.isReadOnly ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          این پرونده فقط قابل مشاهده است و امکان ویرایش وجود ندارد.
        </Alert>
      ) : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            {["firstName", "lastName", "nationalCode", "email", "address", "postalCode", "telephon"].map(
              (field) =>
                isFieldEditable(field) || (detail.isReadOnly && profile[field]) ? (
                  <Grid
                    item
                    xs={12}
                    sm={field === "nationalCode" ? 6 : 6}
                    md={field === "nationalCode" ? 4 : field === "email" ? 8 : 6}
                    key={field}
                  >
                    <TextField
                      fullWidth
                      label={FIELD_LABELS[field]}
                      value={profile[field] || ""}
                      onChange={(e) => setProfile((p) => ({ ...p, [field]: e.target.value }))}
                      InputProps={{ readOnly: !isFieldEditable(field) }}
                    />
                  </Grid>
                ) : null
            )}

            {isFieldEditable("locationInput") || (detail.isReadOnly && profile.locationInput) ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={FIELD_LABELS.locationInput}
                  value={profile.locationInput || ""}
                  onChange={(e) => setProfile((p) => ({ ...p, locationInput: e.target.value }))}
                  InputProps={{ readOnly: !isFieldEditable("locationInput") }}
                  error={Boolean(locationError)}
                  helperText={locationError || undefined}
                />
              </Grid>
            ) : null}

            {locationPreview ? (
              <Grid item xs={12}>
                <Alert severity="success">
                  موقعیت: {Number(locationPreview.latitude).toFixed(6)}،{" "}
                  {Number(locationPreview.longitude).toFixed(6)}
                </Alert>
              </Grid>
            ) : null}

            {["electricBoillNumber", "electricBoillUrl", "homeOwnerShipDocUrl", "note"].map(
              (field) =>
                isFieldEditable(field) || (detail.isReadOnly && request[field]) ? (
                  <Grid item xs={12} sm={6} key={field}>
                    <TextField
                      fullWidth
                      label={FIELD_LABELS[field]}
                      value={request[field] || ""}
                      onChange={(e) => setRequest((r) => ({ ...r, [field]: e.target.value }))}
                      InputProps={{ readOnly: !isFieldEditable(field) }}
                      multiline={field === "note"}
                      minRows={field === "note" ? 2 : 1}
                    />
                  </Grid>
                ) : null
            )}
          </Grid>
        </CardContent>
      </Card>

      {detail.canEdit ? (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            ذخیره اصلاحات
          </Button>
          <Button variant="outlined" color="primary" onClick={handleResubmit} disabled={saving}>
            ارسال برای بررسی مجدد
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default CustomerAssistedRegistrationCorrectionPage;
